import React, { useState, useEffect, useCallback } from 'react';
import { Container, Table, Button, Spinner, Alert, Row, Col, InputGroup, FormControl } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FaEdit, FaTrash, FaPlus, FaSearch } from 'react-icons/fa';
import { productApi } from '../../services/api'; // Make sure path is correct
// Import SweetAlert2
import Swal from 'sweetalert2';

const ProductListPage = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            // Add search/filtering parameters if backend supports it
            const params = { limit: 100 }; // Fetch more products for local filtering for now
            const response = await productApi.getProducts(params);
            setProducts(response.data.items || []);
        } catch (err) {
            console.error("Error fetching products:", err);
            // Ensure the error message is always a string
            let errorMessage = 'Failed to load products.'; // Default message
            const errorDetail = err.response?.data?.detail;
            if (errorDetail) {
                // If detail exists, try to use it. If it's an object, stringify it.
                errorMessage = typeof errorDetail === 'string'
                    ? errorDetail
                    : JSON.stringify(errorDetail);
            } else if (err.message) {
                // Otherwise, fall back to err.message
                errorMessage = err.message;
            }
            setError(String(errorMessage || 'An unexpected error occurred.')); // Ensure we always set a string
        } finally {
            setLoading(false);
        }
    }, []); // No dependencies for now, add search/filters later

    useEffect(() => {
        document.title = 'Admin - Manage Products';
        fetchProducts();
    }, [fetchProducts]);

    const handleDeleteClick = (product) => {
        Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to delete the product "${product.name}"? This cannot be undone!`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            showLoaderOnConfirm: true, // Show loading indicator on the button
            preConfirm: async () => {
                try {
                    await productApi.deleteProduct(product.id);
                    return product.id; // Return deleted product ID on success
                } catch (err) {
                    console.error("Error deleting product:", err);
                    // Ensure the Swal error message is always a string
                    const errorDetail = err.response?.data?.detail;
                    let errorMsg = 'Failed to delete product.';
                    if (errorDetail) {
                       errorMsg = typeof errorDetail === 'string' ? errorDetail : JSON.stringify(errorDetail);
                    } else if (err.message) {
                       errorMsg = err.message;
                    }
                    Swal.showValidationMessage(`Request failed: ${errorMsg}`);
                    return null; // Indicate failure
                }
            },
            allowOutsideClick: () => !Swal.isLoading() // Prevent closing while loading
        }).then((result) => {
            if (result.isConfirmed && result.value) {
                // Update the product list locally
                setProducts(prevProducts => prevProducts.filter(p => p.id !== result.value));
                Swal.fire(
                    'Deleted!',
                    `Product "${product.name}" has been deleted.`,
                    'success'
                );
            }
        }); // End then()
    } // Removed semicolon

    // Simple client-side filtering
    const filteredProducts = products.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.bank?.bank && product.bank.bank.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.category?.category && product.category.category.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const renderTableContent = () => {
        if (loading && products.length === 0) { // Show spinner only on initial load
            return <tr><td colSpan="7" className="text-center"><Spinner animation="border" size="sm" /> Loading...</td></tr>;
        }
        if (error) {
            return <tr><td colSpan="7"><Alert variant="danger">{error}</Alert></td></tr>;
        }
        if (filteredProducts.length === 0) {
            return <tr><td colSpan="7" className="text-center">No products found.</td></tr>;
        }
        return filteredProducts.map(product => (
            <tr key={product.id}>
                <td>{product.id}</td>
                <td>
                    <img src={product.product_image_url || '/static/images/Wishfin-logo.png'} alt={product.name} style={{ width: '50px', height: 'auto', marginRight: '10px' }} onError={(e) => { e.target.onerror = null; e.target.src = '/static/images/Wishfin-logo.png'; }} />
                    {product.name}
                </td>
                <td>{product.bank?.bank || 'N/A'}</td>
                <td>{product.category?.category || 'N/A'}</td>
                <td>{product.is_active ? 'Active' : 'Inactive'}</td>
                <td>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => navigate(`/admin/products/edit/${product.id}`)}
                        title="Edit Product"
                    >
                        <FaEdit />
                    </Button>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteClick(product)}
                        title={`Delete ${product.name}`}
                        disabled={loading} // Disable delete button while another action is in progress
                    >
                        <FaTrash />
                    </Button>
                </td>
            </tr>
        ));
    };

    return (
        <>
            <Container fluid className="p-4">
                <Row className="mb-4 align-items-center">
                    <Col md={6}>
                        <h2 className="page-title">Manage Products</h2>
                    </Col>
                    <Col md={6} className="text-md-end">
                        <Button as={Link} to="/admin/products/new" variant="success">
                            <FaPlus className="me-1" /> Add New Product
                        </Button>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col md={4}>
                        <InputGroup>
                            <InputGroup.Text><FaSearch /></InputGroup.Text>
                            <FormControl
                                placeholder="Search by Name, Bank, Category..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                     {/* Add filtering dropdowns here if needed */}
                </Row>

                <Table striped bordered hover responsive className="admin-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Name</th>
                            <th>Bank</th>
                            <th>Category</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {renderTableContent()}
                    </tbody>
                </Table>

                {/* SweetAlert2 handles confirmation, no modal component needed here */}

            </Container>
        </>
    );
};

export default ProductListPage;

